import { reloadData } from '@axo/form/data-access/backend-sync';
import { IAuthProviderResponse } from '@axo/shared/auth/providers';
import { DataFetchedFrom } from '@axo/shared/data-access/types';
import { useCallback, useMemo } from 'react';
import { useAuthenticationContext } from '../../../../context/Authentication/AuthenticationContext';
import { useAxoFormContext } from '../../../../context/AxoForm/AxoForm';
import type { ESkatData } from '../@types/ESkat';

export const useESkat = () => {
  const { applicationId, customerJWT } = useMemo(() => reloadData.get(), []);

  const { baseUrl, onChange, reloadSyncData } = useAxoFormContext<ESkatData>();

  const {
    setAuthentication,
    loading,
    setLoading,
    debtProviderData,
    setDebtProviderData,
  } = useAuthenticationContext();

  const hasFetchedDebts = useMemo(
    () =>
      debtProviderData &&
      (debtProviderData as ESkatData)?.UpdatedLoanFields.includes('Debts'),
    [debtProviderData]
  );

  const onESkatError = (response: IAuthProviderResponse) => {
    console.error(
      `Error: Provider: ${response.provider} - ${response.errorMessage}`
    );
  };

  const onESkatSuccess = useCallback(
    async (response: IAuthProviderResponse) => {
      if (!customerJWT) {
        throw new Error('ESkat failed, missing JWT');
      }

      setLoading(true);

      try {
        // call Case State to check if we can update Loan Application
        const caseStateResponse = await fetch(
          `${baseUrl}/uniify/case-state?loanApplicationID=${applicationId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: customerJWT,
            },
          }
        );
        let caseState = await caseStateResponse.json();

        const shouldUpdate = !caseState.HasCaseData;

        if (shouldUpdate) {
          // Call update-case
          await fetch(`${baseUrl}/uniify/update-case`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: customerJWT,
            },
            body: JSON.stringify({
              LoanApplicationID: applicationId,
            }),
          });

          // Call Case State again to grab updated Fields
          const response = await fetch(
            `${baseUrl}/uniify/case-state?loanApplicationID=${applicationId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: customerJWT,
              },
            }
          );
          caseState = await response.json();
        }

        setAuthentication(customerJWT, response.provider);
        setDebtProviderData({
          HasCaseData: caseState.HasCaseData,
          UpdatedAt: caseState.UpdatedAt,
          // Save only the keys as the display value is different from the backend values
          // it needs to go through the mutators.fromApi to be converted from backend -> frontend
          UpdatedLoanFields: Object.keys(caseState.LoanApplicationPatch),
          UpdatedPersonFields: Object.keys(caseState.PersonPatch),
        });

        // Reload the form data if it was updated
        if (shouldUpdate) await reloadSyncData();

        await onChange({
          'LoanApplication/DataFetchedFrom': DataFetchedFrom.Uniify,
        } as unknown as ESkatData);
      } catch (error) {
        if (error instanceof Error) {
          throw new Error(error.message);
        }
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseUrl, customerJWT, applicationId]
  );

  return {
    logged: !!debtProviderData && !loading,
    eSkatData: debtProviderData as ESkatData | undefined,
    hasFetchedDebts,
    loading,
    onESkatError,
    onESkatSuccess,
  };
};
